import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { supabase } from '../../services/supabase';
import { assignUsersToEvent } from '../../services/supabase';
import { PrimaryButton, DefaultButton } from '../Common/BaseButton';
const FormContainer = styled.div`
  ${props => props.$isEventPage && `
    margin-top: 1rem;
  `}
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #2D882D;
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const LoadingText = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const StyledSelect = styled.div`
  .react-select__control {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 0.2rem;
    box-shadow: none;

    @media (max-width: 768px) {
      font-size: 12px;
    }
    
    &:hover {
      border-color: #2D882D;
    }
  }
  
  .react-select__control--is-focused {
    border-color: #2D882D !important;
    box-shadow: 0 0 0 1px #2D882D !important;
  }
  
  .react-select__option--is-selected {
    background-color: #2D882D;
  }
  
  .react-select__option--is-focused {
    background-color: rgba(45, 136, 45, 0.1);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: flex-end;
`;

const EventAssignUserForm = ({
  eventId,
  employees,
  assignedUsers = [],
  onAssignChange,
  onSuccess,
  onCancel,
  isVisible,
  isEventPage
}) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingUsers, setFetchingUsers] = useState(true);

  // Fetch users only if not provided via props
  useEffect(() => {
    if (employees) {
      setUsers(employees);
      setFetchingUsers(false);
    } else {
      const fetchUsers = async () => {
        try {
          setFetchingUsers(true);
          const { data: allUsers, error: usersError } = await supabase.rpc('get_users_with_name');
          if (usersError) throw usersError;
          setUsers(allUsers || []);
        } catch (err) {
          console.error('Failed to fetch users:', err);
          setError('Failed to load users. Please try again.');
        } finally {
          setFetchingUsers(false);
        }
      };

      fetchUsers();
    }
  }, [employees, eventId]);

  // Filter out already assigned users
  const availableUsers = useMemo(() => {
    return users.filter(user => {
      if (!eventId) {
        return !selectedUsers.some(sel => sel.value === user.id);
      }
      return !assignedUsers.some(assigned => assigned.user?.id === user.id);
    });
  }, [users, selectedUsers, assignedUsers, eventId]);

  // For react-select
  const userOptions = useMemo(() => {
    return availableUsers.map(user => {
      const label = `${user.name} (${user.email})`;
      return { value: user.id, label, _l: label.toLowerCase() };
    });
  }, [availableUsers]);

  const handleAssign = async () => {
    try {
      setLoading(true);
      setError(null);

      if (eventId) {
        // For existing events (EventDetailsPage)
        const { data, error } = await assignUsersToEvent(eventId, selectedUsers);
        if (error) throw error;
        
        // Clear the selected users after successful assignment
        setSelectedUsers([]);
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        // For new events (EventForm)
        onAssignChange(selectedUsers);
      }
    } catch (err) {
      console.error('Failed to assign users:', err);
      setError('Failed to assign users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <FormContainer $isEventPage={isEventPage}>
      <Label>Add Team Member(s)</Label>
      
      {fetchingUsers ? (
        <LoadingText>Loading users...</LoadingText>
      ) : (
        <div>
          <StyledSelect>
          <Select
            className="react-select"
            classNamePrefix="react-select"
            options={userOptions}
            value={selectedUsers}
            onChange={(newValue) => {
              setSelectedUsers(newValue);
              if (!eventId) {
                onAssignChange(newValue);
              }
            }}
            placeholder="Select users..."
            isMulti
            isClearable
            menuPortalTarget={document.body}
            filterOption={(option, rawInput) =>
              option.data._l.includes((rawInput || '').toLowerCase())
            }
          />
          </StyledSelect>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          {/* Only show buttons if we're in the event details page */}
          {eventId && (
            <ButtonRow>
              <DefaultButton 
                type="button" 
                variant="cancel"
                onClick={onCancel}
              >
                Cancel
              </DefaultButton>
              <PrimaryButton 
                type="button" 
                onClick={handleAssign}
                disabled={loading || !selectedUsers.length}
              >
                {loading ? 'Assigning...' : 'Assign to Event'}
              </PrimaryButton>
            </ButtonRow>
          )}
        </div>
      )}
    </FormContainer>
  );
};

export default EventAssignUserForm;